import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Store, Hand, Truck, PackageX } from "lucide-react";
import { PublicLayout } from "../components/PublicLayout";
import { ProductImage, ProductGrid } from "../components/shop/ProductCard";
import { BuyCard } from "../components/shop/BuyCard";
import { OrganizerContactBlock } from "../components/OrganizerContactBlock";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { api } from "../lib/api";
import { nok, shopUrl } from "../lib/shop";
import { useLang } from "../context/LanguageContext";

// Varedetalj + kasse: /butikk/:organizerId/vare/:productId (mobil: bilete → kjøpskort → skildring; desktop: 7/5 med sticky kjøpskort)
export default function ShopProductPage() {
  const { organizerId, productId } = useParams();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    window.scrollTo({ top: 0 });
    api
      .get(`/shop/organizers/${organizerId}/products/${productId}`)
      .then((res) => setData(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [organizerId, productId]);

  if (loading) {
    return (
      <PublicLayout>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="product-page-loading">
          <div className="lg:col-span-7 space-y-4"><Skeleton className="aspect-[4/3] w-full rounded-2xl" /><Skeleton className="h-8 w-2/3" /><Skeleton className="h-24 w-full" /></div>
          <div className="lg:col-span-5"><Skeleton className="h-[520px] rounded-2xl" /></div>
        </div>
      </PublicLayout>
    );
  }

  if (notFound || !data) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto px-4 py-24 text-center space-y-4" data-testid="product-not-found">
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-secondary text-muted-foreground"><PackageX className="w-7 h-7" /></span>
          <h1 className="text-2xl font-semibold">{t("Fant ikke varen")}</h1>
          <p className="text-sm text-muted-foreground">{t("Varen er ikke tilgjengelig lenger.")}</p>
          <div className="flex flex-wrap justify-center gap-2 pt-2">
            <Button asChild data-testid="product-not-found-shop-link"><Link to={shopUrl(organizerId)}><ArrowLeft className="w-4 h-4 mr-1.5" />{t("Se butikken")}</Link></Button>
            <Button asChild variant="outline" data-testid="product-not-found-all-link"><Link to="/butikk">{t("Til alle butikker")}</Link></Button>
          </div>
        </div>
      </PublicLayout>
    );
  }

  const { product, organizer, more } = data;
  const shop = data;

  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 space-y-10" data-testid="product-page">
        <Link to={shopUrl(organizer.id)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150" data-testid="product-back-link">
          <ArrowLeft className="w-4 h-4" />
          {t("Butikken til {org}", { org: organizer.org_name })}
        </Link>

        {/* Éin BuyCard-instans: mobil = bilete → kjøpskort → skildring (order), desktop = 7/5 med kjøpskort sticky i høgre kolonne over to rader */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="order-1 lg:col-span-7 space-y-6 min-w-0">
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-muted border border-border" data-testid="product-image-wrap">
              <ProductImage product={product} className={product.stock_status === "sold_out" ? "opacity-70" : ""} />
            </div>
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <Link to={shopUrl(organizer.id)} className="inline-flex items-center gap-1.5 hover:text-foreground underline-offset-4 hover:underline transition-colors duration-150" data-testid="product-organizer-link">
                  <Store className="w-4 h-4" /> {t("Selges av")} {organizer.org_name}
                </Link>
                {product.sku && <span className="font-mono text-xs" data-testid="product-sku">· {t("Varenr. {sku}", { sku: product.sku })}</span>}
              </div>
              <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="product-title">{product.name}</h1>
              <p className="text-xl font-heading font-semibold tabular-nums lg:hidden" data-testid="product-price-mobile">{product.price === 0 ? t("Gratis") : nok(product.price)}</p>
            </div>
          </motion.div>

          <div className="order-2 lg:col-span-5 lg:row-span-2">
            <div className="lg:sticky lg:top-24"><BuyCard product={product} shop={shop} /></div>
          </div>

          <div className="order-3 lg:col-span-7 space-y-6 min-w-0">
            {product.description ? (
              <div className="space-y-2" data-testid="product-description">
                <h2 className="text-lg font-heading font-semibold">{t("Om varen")}</h2>
                <p className="text-[15px] leading-relaxed whitespace-pre-line text-foreground/90">{product.description}</p>
              </div>
            ) : null}

            <div className="grid gap-3 sm:grid-cols-2 text-sm" data-testid="product-delivery-info">
              {shop.pickup_enabled && (
                <div className="rounded-xl border border-border bg-card p-4 flex gap-3">
                  <Hand className="w-5 h-5 text-brand shrink-0 mt-0.5" />
                  <div><p className="font-medium">{t("Henting hos arrangøren")}</p><p className="text-muted-foreground">{shop.pickup_place || t("Hentes etter avtale hos arrangøren")}</p></div>
                </div>
              )}
              {shop.shipping_enabled && (
                <div className="rounded-xl border border-border bg-card p-4 flex gap-3">
                  <Truck className="w-5 h-5 text-brand shrink-0 mt-0.5" />
                  <div><p className="font-medium">{t("Sending i posten")}</p><p className="text-muted-foreground">{Number(shop.shipping_price || 0) === 0 ? t("Gratis frakt") : t("Frakt {price}", { price: nok(shop.shipping_price) })}</p></div>
                </div>
              )}
            </div>

            <OrganizerContactBlock name={organizer.org_name} orgNumber={organizer.org_number} contact={organizer.contact} compact />
          </div>
        </div>

        {more?.length > 0 && (
          <section className="space-y-4" data-testid="product-more-section">
            <h2 className="text-lg sm:text-xl font-heading font-semibold tracking-tight">{t("Flere varer fra {org}", { org: organizer.org_name })}</h2>
            <ProductGrid products={more} showOrganizer={false} testId="product-more-grid" />
          </section>
        )}
      </div>
    </PublicLayout>
  );
}
